import { format, parseISO } from 'date-fns'
import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router'
import { Prose } from '#app/components/brand/prose.tsx'
import { Eyebrow, Heading } from '#app/components/brand/section.tsx'
import {
	type Faq,
	type Frontmatter,
	type RelatedLink,
} from '#app/utils/content.ts'
import { Footer } from './footer'
import Header from './header'

type TocItem = { id: string; text: string; level: number }

/** Stable, human-readable anchor id from a question (for TOC + deep links). */
function faqSlug(question: string) {
	return (
		'faq-' +
		question
			.toLowerCase()
			.replace(/[^a-z0-9]+/g, '-')
			.replace(/^-+|-+$/g, '')
			.slice(0, 60)
	)
}

/**
 * Visible FAQ block. The answer text here is the SAME string emitted as
 * FAQPage JSON-LD in the route, so the rich result always matches the page.
 * The <h2 id="faq"> and per-question <h3> ids feed the article TOC.
 */
function FaqSection({ faqs }: { faqs: Faq[] }) {
	return (
		<section className="border-hairline mt-16 border-t pt-10">
			<h2
				id="faq"
				className="font-display text-ink mb-6 text-[28px] font-bold tracking-[-0.02em]"
			>
				Frequently asked questions
			</h2>
			<dl className="flex flex-col gap-6">
				{faqs.map((f) => (
					<div key={f.q}>
						<dt>
							<h3
								id={faqSlug(f.q)}
								className="font-display text-ink text-[19px] font-semibold"
							>
								{f.q}
							</h3>
						</dt>
						<dd className="mt-2 text-[17px] leading-relaxed text-[#4A4952]">
							{f.a}
						</dd>
					</div>
				))}
			</dl>
		</section>
	)
}

/** Curated "keep reading" grid — the scalable interlinking surface. */
function RelatedSection({ related }: { related: RelatedLink[] }) {
	return (
		<section className="mt-14">
			<h2 className="font-display text-ink mb-5 text-[22px] font-bold tracking-[-0.02em]">
				Keep reading
			</h2>
			<div className="grid gap-4 sm:grid-cols-2">
				{related.map((r) => (
					<Link
						key={r.to}
						to={r.to}
						className="border-hairline hover:border-brand group rounded-2xl border bg-white p-5 no-underline transition-colors"
					>
						<div className="font-display text-ink group-hover:text-brand text-[17px] font-semibold transition-colors">
							{r.title}
						</div>
						{r.description ? (
							<p className="mt-1.5 mb-0 text-[14.5px] leading-snug text-[#8A867D]">
								{r.description}
							</p>
						) : null}
					</Link>
				))}
			</div>
		</section>
	)
}

/**
 * Visible breadcrumb (Home › Section › current). Mirrors the BreadcrumbList
 * JSON-LD emitted by the $slug routes, reinforcing it for users and crawlers.
 */
function Breadcrumbs({
	sectionTo,
	sectionLabel,
	title,
}: {
	sectionTo: string
	sectionLabel: string
	title: string
}) {
	return (
		<nav aria-label="Breadcrumb" className="mb-6">
			<ol className="flex flex-wrap items-center justify-center gap-1.5 text-[13px] text-[#8A867D]">
				<li>
					<Link to="/" className="hover:text-ink">
						Home
					</Link>
				</li>
				<li aria-hidden>›</li>
				<li>
					<Link to={sectionTo} className="hover:text-ink">
						{sectionLabel}
					</Link>
				</li>
				<li aria-hidden>›</li>
				<li
					className="text-ink max-w-[60vw] truncate font-medium"
					aria-current="page"
				>
					{title}
				</li>
			</ol>
		</nav>
	)
}

/**
 * Parse date-only ISO strings without timezone shift — new Date('2026-07-01')
 * is UTC midnight and can render as the previous month west of UTC.
 */
function formatDate(iso?: string) {
	if (!iso) return null
	try {
		const d = parseISO(iso)
		if (Number.isNaN(d.getTime())) return null
		return format(d, 'MMM yyyy')
	} catch {
		return null
	}
}

/**
 * Long-form reading layout shared by guides and comparisons: a scroll-progress
 * bar, an editorial hero, and a sticky table of contents built from the
 * article's own headings (rehype-slug provides the ids).
 */
export function ArticleLayout({
	frontmatter,
	backTo,
	backLabel,
	children,
}: {
	frontmatter: Frontmatter
	backTo: string
	backLabel: string
	children: React.ReactNode
}) {
	const articleRef = useRef<HTMLDivElement>(null)
	const [progress, setProgress] = useState(0)
	const [toc, setToc] = useState<TocItem[]>([])
	const [activeId, setActiveId] = useState<string>('')

	// Build the TOC from the rendered headings. A MutationObserver re-runs this
	// when a lazily-loaded article body mounts after the initial render (client
	// navigation), so the TOC isn't left empty.
	useEffect(() => {
		const el = articleRef.current
		if (!el) return
		const build = () => {
			const headings = Array.from(el.querySelectorAll('h2, h3')).filter(
				(h): h is HTMLHeadingElement => h.id !== '',
			)
			setToc(
				headings.map((h) => ({
					id: h.id,
					text: h.textContent ?? '',
					level: h.tagName === 'H3' ? 3 : 2,
				})),
			)
		}
		build()
		const observer = new MutationObserver(build)
		observer.observe(el, { childList: true, subtree: true })
		return () => observer.disconnect()
	}, [children])

	// Scroll progress + active-section highlight.
	useEffect(() => {
		const onScroll = () => {
			const el = articleRef.current
			if (!el) return
			const top = el.offsetTop
			const height = el.offsetHeight - window.innerHeight
			const scrolled = window.scrollY - top
			setProgress(
				Math.max(0, Math.min(100, (scrolled / Math.max(1, height)) * 100)),
			)

			const headings = Array.from(el.querySelectorAll('h2, h3')).filter(
				(h): h is HTMLHeadingElement => h.id !== '',
			)
			let current = ''
			for (const h of headings) {
				if (h.getBoundingClientRect().top <= 120) current = h.id
			}
			setActiveId(current)
		}
		onScroll()
		window.addEventListener('scroll', onScroll, { passive: true })
		return () => window.removeEventListener('scroll', onScroll)
	}, [toc])

	const dateLabel = formatDate(frontmatter.updated ?? frontmatter.date)

	return (
		<div className="bg-canvas text-ink min-h-screen font-sans">
			<div
				className="bg-brand fixed top-0 left-0 z-50 h-[3px] transition-[width] duration-100"
				style={{ width: `${progress}%` }}
			/>
			<Header />

			<main>
				<div className="relative isolate overflow-hidden">
					{frontmatter.image ? (
						<img
							src={frontmatter.image}
							alt={frontmatter.title}
							width={1200}
							height={630}
							className="pointer-events-none absolute inset-0 -z-10 h-full w-full [mask-image:radial-gradient(75%_68%_at_50%_36%,black,transparent_74%)] object-cover opacity-[0.16]"
						/>
					) : null}
					<div className="relative mx-auto max-w-[720px] px-7 pt-14 pb-2.5 text-center">
						<Breadcrumbs
							sectionTo={backTo}
							sectionLabel={
								backTo === '/best-tools'
									? 'Best tools'
									: backTo === '/guides'
										? 'Guides'
										: backLabel
							}
							title={frontmatter.title}
						/>
						{frontmatter.tag ? (
							<Eyebrow className="mb-5 justify-center">
								{frontmatter.tag}
							</Eyebrow>
						) : null}
						<Heading
							as="h1"
							className="text-[clamp(34px,5.2vw,52px)] leading-[1.05] tracking-[-0.03em]"
						>
							{frontmatter.title}
						</Heading>
						<p className="mx-auto mt-5 mb-7 text-xl leading-relaxed text-pretty text-[#57565E]">
							{frontmatter.description}
						</p>
						<div className="flex items-center justify-center gap-3.5 text-sm text-[#8A867D]">
							{frontmatter.author ? (
								<>
									{frontmatter.author === 'Brad Williams' ? (
										<Link
											to="/authors/brad-williams"
											className="hover:text-brand font-medium text-[#2B2A31]"
										>
											{frontmatter.author}
										</Link>
									) : (
										<span className="font-medium text-[#2B2A31]">
											{frontmatter.author}
										</span>
									)}
								</>
							) : null}
							{dateLabel ? (
								<>
									<span>·</span>
									<span>Updated {dateLabel}</span>
								</>
							) : null}
							{frontmatter.readingTime ? (
								<>
									<span>·</span>
									<span>{frontmatter.readingTime}</span>
								</>
							) : null}
						</div>
					</div>
				</div>

				<div className="mx-auto grid max-w-[1200px] items-start gap-14 px-8 py-10 pb-24 lg:grid-cols-[minmax(0,1fr)_240px]">
					<div
						ref={articleRef}
						className="w-full max-w-[760px] justify-self-center"
					>
						<Prose className="text-[18.5px]">{children}</Prose>
						{frontmatter.faqs?.length ? (
							<FaqSection faqs={frontmatter.faqs} />
						) : null}
						{frontmatter.related?.length ? (
							<RelatedSection related={frontmatter.related} />
						) : null}
						<div className="border-hairline mt-14 border-t pt-8">
							<Link
								to={backTo}
								className="text-brand text-[15px] font-semibold"
							>
								← {backLabel}
							</Link>
						</div>
					</div>

					{toc.length > 0 ? (
						<aside className="sticky top-24 hidden lg:block">
							<div className="mb-3 font-mono text-[11px] font-semibold tracking-[0.08em] text-[#8A867D] uppercase">
								On this page
							</div>
							<nav className="flex flex-col gap-2 border-l border-[#EAE5DC]">
								{toc.map((item) => (
									<a
										key={item.id}
										href={`#${item.id}`}
										className={`-ml-px border-l-2 py-0.5 text-[14px] leading-snug transition-colors ${
											item.level === 3 ? 'pl-6' : 'pl-4'
										} ${
											activeId === item.id
												? 'border-brand text-brand font-medium'
												: 'hover:text-ink border-transparent text-[#8A867D]'
										}`}
									>
										{item.text}
									</a>
								))}
							</nav>
						</aside>
					) : null}
				</div>
			</main>

			<Footer />
		</div>
	)
}
